import { defineStore } from 'pinia'
import { ref } from 'vue'
import * as systemApi from '@/api/system'
import type { SystemConfig, FeatureConfig } from '@/api/system'

/**
 * 系统配置Store
 */
export const useSystemStore = defineStore('system', () => {
  // 状态
  const configs = ref<SystemConfig[]>([])
  const featureConfig = ref<FeatureConfig>({
    enable3DDesign: true,
    enable2DDesign: true,
    defaultDesignMode: '2d'
  })

  /**
   * 加载所有配置
   */
  async function loadAllConfigs(): Promise<void> {
    try {
      const response = await systemApi.getAllConfigs()
      configs.value = response.data.data
    } catch (error) {
      console.error('加载配置失败:', error)
      throw error
    }
  }

  /**
   * 加载功能配置
   */
  async function loadFeatureConfig(): Promise<void> {
    try {
      const response = await systemApi.getFeatureConfig()
      featureConfig.value = response.data.data
      // 保存到localStorage
      localStorage.setItem('featureConfig', JSON.stringify(response.data.data))
    } catch (error) {
      console.error('加载功能配置失败:', error)
      // 尝试从localStorage加载
      const cached = localStorage.getItem('featureConfig')
      if (cached) {
        featureConfig.value = JSON.parse(cached)
      }
    }
  }

  /**
   * 根据配置键获取配置
   */
  async function getConfigByKey(configKey: string): Promise<SystemConfig | null> {
    try {
      const response = await systemApi.getConfigByKey(configKey)
      return response.data.data
    } catch (error) {
      console.error('获取配置失败:', error)
      return null
    }
  }

  /**
   * 根据配置分组获取配置列表
   */
  async function getConfigsByGroup(group: string): Promise<SystemConfig[]> {
    try {
      const response = await systemApi.getConfigsByGroup(group)
      return response.data.data
    } catch (error) {
      console.error('获取配置分组失败:', error)
      return []
    }
  }

  /**
   * 更新配置
   */
  async function updateConfig(id: number, config: SystemConfig): Promise<boolean> {
    try {
      await systemApi.updateConfig(id, config)
      await loadFeatureConfig() // 重新加载功能配置
      return true
    } catch (error) {
      console.error('更新配置失败:', error)
      return false
    }
  }

  /**
   * 批量更新配置
   */
  async function batchUpdateConfig(configMap: Record<string, string>): Promise<boolean> {
    try {
      await systemApi.batchUpdateConfig(configMap)
      await loadFeatureConfig() // 重新加载功能配置
      return true
    } catch (error) {
      console.error('批量更新配置失败:', error)
      return false
    }
  }

  /**
   * 创建配置
   */
  async function createConfig(config: SystemConfig): Promise<boolean> {
    try {
      await systemApi.createConfig(config)
      await loadAllConfigs()
      return true
    } catch (error) {
      console.error('创建配置失败:', error)
      return false
    }
  }

  /**
   * 删除配置
   */
  async function deleteConfig(id: number): Promise<boolean> {
    try {
      await systemApi.deleteConfig(id)
      await loadAllConfigs()
      return true
    } catch (error) {
      console.error('删除配置失败:', error)
      return false
    }
  }

  /**
   * 初始化默认配置
   */
  async function initDefaultConfig(): Promise<boolean> {
    try {
      await systemApi.initDefaultConfig()
      await loadAllConfigs()
      await loadFeatureConfig()
      return true
    } catch (error) {
      console.error('初始化默认配置失败:', error)
      return false
    }
  }

  /**
   * 从localStorage加载缓存的功能配置
   */
  function loadCachedFeatureConfig(): void {
    const cached = localStorage.getItem('featureConfig')
    if (cached) {
      try {
        featureConfig.value = JSON.parse(cached)
      } catch (error) {
        console.error('解析缓存配置失败:', error)
      }
    }
  }

  /**
   * 初始化
   */
  function init(): void {
    loadCachedFeatureConfig()
    loadFeatureConfig().catch(console.error)
  }

  // 初始化
  init()

  return {
    // 状态
    configs,
    featureConfig,

    // 方法
    loadAllConfigs,
    loadFeatureConfig,
    getConfigByKey,
    getConfigsByGroup,
    updateConfig,
    batchUpdateConfig,
    createConfig,
    deleteConfig,
    initDefaultConfig,
    init
  }
})
